/**
 * THE DOCKED CONSOLE, as a shape rather than as one screen's furniture.
 *
 * Three passes now put a commissioner's actions in the same bar — the Rules docket, the lottery
 * launcher, and the docket redesign after them — and the handoffs are explicit that this is one
 * pattern and not three that resemble each other: "the same pattern as the Rules console, so one
 * behaviour covers every consequential button in the app".
 *
 * So the geometry and the arming step live here, and each screen supplies only what it is about.
 * What the geometry asserts, and why it is worth having in one place:
 *
 *   COLLAPSE AT THE FAR LEFT, the irreversible act at the far right past a divider, the reversible
 *   ones clustered between. The two controls with the most different consequences are the two
 *   furthest apart.
 *
 *   THE PRIMARY ARMS RATHER THAN FIRES. It swaps the bar to a warmer fill behind a 4px rail and
 *   states the consequence in the space the reversible actions were using — so what you read is
 *   where what you pressed was.
 *
 *   THE COLLAPSED BAR STATES AN OBLIGATION, never a count of its own buttons.
 *
 * Docked through `dock.tsx` for the reason that file documents, and collapsed by default on a
 * phone, where the open bar is three rows over the screen it is a console for.
 */

import { useRef, useState, type ReactNode } from 'react';
import { managerShort } from '../domain/identity.js';
import { GmBadge } from './GmBadge.js';
import { dock } from './dock.js';
import { useDockedHeight } from './useDockedHeight.js';
import { useMedia } from './useMedia.js';

export interface ConsoleAction {
  id: string;
  label: string;
}

export interface ConsoleProps {
  /** Who is acting. The bar names him — several people can, and everybody is affected. */
  actor: string;
  /** Where things stand, on the open bar. */
  line: string;
  /** The pending obligation, on the collapsed bar. Not a count. */
  standing: string;
  /** The one state-changing action. Empty renders no primary at all. */
  primary: string;
  /** The arming question and what confirming will do. */
  question: string;
  consequence: string;
  /** Why the primary cannot fire. Empty when it can. */
  blocked?: string;
  reversible?: readonly ConsoleAction[];
  /** Dark while the screen above it is. */
  dark?: boolean;
  /**
   * Skip the arming step. Only for a primary that changes nothing a person would want back — the
   * lottery's `End the rehearsal` is the case it exists for.
   */
  immediate?: boolean;
  onPrimary: () => void;
  onAction?: (id: string) => void;
  /** Anything extra to sit beside the state line. */
  aside?: ReactNode;
}

export function Console(props: ConsoleProps) {
  const phone = useMedia('(max-width: 760px)');
  const [open, setOpen] = useState(!phone);
  const [arming, setArming] = useState(false);
  const bar = useRef<HTMLDivElement>(null);
  useDockedHeight(bar);

  const tone = `cconsole ${props.dark ? 'dark' : ''} ${arming ? 'arming' : ''}`;
  const reversible = props.reversible ?? [];

  if (!open) {
    return dock(
      <div className={`${tone} shut`} ref={bar}>
        <button type="button" className="cconsoleopen" onClick={() => setOpen(true)}>
          <Chevron up />
          <span className="cconsolewho">Commissioner</span>
          <span className="cconsolestanding">{props.standing}</span>
        </button>
      </div>,
    );
  }

  if (arming) {
    return dock(
      <div className={tone} ref={bar} role="group" aria-label="Confirm">
        <div className="cconsolearm">
          <p className="cconsoleq">{props.question}</p>
          <p className="cconsolewhat">{props.consequence}</p>
        </div>
        <span className="cconsoleacts">
          <button type="button" className="cconsolecancel" onClick={() => setArming(false)}>
            Cancel
          </button>
          <button type="button" className="cconsoleprimary"
            onClick={() => { setArming(false); props.onPrimary(); }}>
            {props.primary}
          </button>
        </span>
      </div>,
    );
  }

  return dock(
    <div className={tone} ref={bar}>
      <button type="button" className="cconsolehide" onClick={() => setOpen(false)}
        aria-label="Collapse the commissioner bar">
        <Chevron />
      </button>

      <span className="cconsoleactor">
        <GmBadge gm={props.actor} />
        <span>
          <b>{managerShort(props.actor)}</b>
          <i>Commissioner</i>
        </span>
      </span>

      <span className="cconsolerule" aria-hidden="true" />

      <span className="cconsolestate">{props.line}{props.aside}</span>

      {reversible.length > 0 && (
        <span className="cconsolegroup">
          {reversible.map((r) => (
            <button key={r.id} type="button" className="cconsolebtn"
              onClick={() => props.onAction?.(r.id)}>
              {r.label}
            </button>
          ))}
        </span>
      )}

      {/* Past the divider, alone. */}
      {props.primary && (
        <button type="button" className="cconsoleprimary" disabled={Boolean(props.blocked)}
          title={props.blocked || undefined}
          onClick={() => (props.immediate ? props.onPrimary() : setArming(true))}>
          {props.primary}
          {!props.immediate && <span aria-hidden="true">…</span>}
        </button>
      )}
    </div>,
  );
}

/** On the same 24 grid and 1.5 stroke as `NavIcons`, so it is not a second icon language. */
function Chevron({ up = false }: { up?: boolean }) {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor"
      strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"
      style={up ? undefined : { transform: 'rotate(180deg)' }}>
      <path d="M6 15l6-6 6 6" />
    </svg>
  );
}
